import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { startInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/start-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import { updateShowOtaAcceptAndCreateBackupProcessModal } from "@src/reducers/your-reducer";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";

export const useOtaAcceptAndCreateBackupProcessModal = (
  data: ActiveInstallationProcessPayload,
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const [isAcceptAndCreateBackupLoading, setIsAcceptAndCreateBackupLoading] =
    useState(false);

  const texts = {
    closeModalAriaLabel: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.closeModalAriaLabel",
    ),
    title: i18n("modules.otaAcceptAndCreateBackupProcessModal.title"),
    targetPathLabel: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.targetPathLabel",
    ),
    sourceMachineLabel: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.sourceMachineLabel",
    ),
    backupPathContainsSectionTitle: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.backupPathContainsSectionTitle",
    ),
    warningText: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.warningText",
    ),
    cancelButtonText: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.cancelButtonText",
    ),
    acceptAndCreateBackupButtonText: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.acceptAndCreateBackupButtonText",
    ),
    startSuccessMessage: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.startSuccessMessage",
    ),
    startFailureMessage: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.startFailureMessage",
    ),
    startErrorMessage: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.startErrorMessage",
    ),
    waitMessage: i18n(
      "modules.otaAcceptAndCreateBackupProcessModal.waitMessage",
    ),
  };

  const machine = {
    name: source?.name,
    ip: source?.dsn,
  };

  const targetPath = data?.target_path;

  const isWaitingForFolderStructure =
    data?.update_details?.folder_structure_result === null &&
    data?.update_details?.folder_structure_error === null;

  const handleCloseModal = () => {
    dispatch(updateShowOtaAcceptAndCreateBackupProcessModal(false));
  };

  const handleAcceptAndCreateBackup = async () => {
    if (!data?.update_id) {
      errorSnackbar(texts.startErrorMessage);
      return;
    }

    setIsAcceptAndCreateBackupLoading(true);

    try {
      const response = await startInstallationProcessUseCase(data.update_id);

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from startInstallationProcessUseCase (accept and create backup modal):",
          response,
        );

      if (response.success) {
        successSnackbar(texts.startSuccessMessage, 5000);
        setInstallAndActionBlock(null);
        handleCloseModal();
        return;
      }

      errorSnackbar(response.error || texts.startFailureMessage, 5000);
      setIsAcceptAndCreateBackupLoading(false);
    } catch (error) {
      console.error(error);
      errorSnackbar(texts.startErrorMessage, 5000);
      setIsAcceptAndCreateBackupLoading(false);
    }
  };

  return {
    texts,
    machine,
    targetPath,
    isWaitingForFolderStructure,
    target_path_folders:
      data?.update_details?.folder_structure_result?.entries?.map(
        (entry) => entry?.path,
      ) ?? [],
    handleCloseModal,
    handleAcceptAndCreateBackup,
    isAcceptAndCreateBackupLoading,
  };
};
